import { VuexModule, Module, Mutation, Action } from "vuex-class-modules";
import {
  AssetsInterface,
  FolderDataInterface,
  responseInterface,
  FoldersWithAssetsInterface,
  TagsInterface,
  FiltersInterface,
} from "@/types";
import {
  associateAssetsWithAlbums,
  getAlbumsAssociatedWithAssets,
  filterAssets,
  generateAlbumTree,
  clearFilters,
} from "@/utils/helpers";
import { usersModule } from "@/store";
import api from "@/apiClient";

@Module
export default class AssetsDataModule extends VuexModule {
  private assets: AssetsInterface[] = [];
  private albums: FolderDataInterface[] = [];
  private albumsWithoutAssets: FolderDataInterface[] = [];
  private filteredAlbums: FoldersWithAssetsInterface[] = [];
  private pinnedAssets: AssetsInterface[] = [];
  private tags: TagsInterface[] = [];
  private globalLoader = false;
  private allDataLoader = false;
  private assetsLoader = false;
  private pinnedAssetsLoader = true;
  private filters: FiltersInterface = {
    assetTypes: [],
    searchQuery: "",
    tags: [],
    categories: [],
  };

  @Action
  public async Load(lang: string): Promise<void> {
    this.setAssetsLoader(true);

    try {
      const response: responseInterface = await api.assets.fetch(lang);

      if (response.statusCode !== 200 || !Array.isArray(response.data)) {
        throw new Error("Assets response is invalid");
      }

      this.setAssetsData(response.data);
    } catch (error) {
      console.error("Failed to load assets:", error);
    } finally {
      this.setAssetsLoader(false);
    }
  }

  @Action
  public async LoadAlbums(lang: string): Promise<void> {
    this.setGlobalLoader(true);

    try {
      const response: responseInterface = await api.assets.fetchAlbums(lang);

      if (response.statusCode !== 200 || !Array.isArray(response.data)) {
        throw new Error("Albums response is invalid");
      }

      const associatedAlbums = associateAssetsWithAlbums(response.data, this.getAssets ?? []);

      this.setAlbumsAssociatedWithAssetsData(associatedAlbums);
      this.setAlbumsData(response.data);
    } catch (error) {
      console.error("Failed to load albums:", error);
    } finally {
      this.setGlobalLoader(false);
    }
  }

  @Action
  public async LoadTags(lang: string): Promise<void> {
    this.setGlobalLoader(true);

    try {
      const response: responseInterface = await api.assets.fetchTags(lang);

      if (response.statusCode !== 200 || !Array.isArray(response.data)) {
        throw new Error("Tags response is invalid");
      }

      this.setTagsData(response.data);
    } catch (error) {
      console.error("Failed to load tags:", error);
    } finally {
      this.setGlobalLoader(false);
    }
  }

  @Action
  public async LoadAssetResourcesByLang(lang: string): Promise<void> {
    this.setAllDataLoader(true);

    try {
      await Promise.all([this.LoadTags(lang), this.Load(lang)]);
      await this.LoadAlbums(lang);
    } finally {
      this.setAllDataLoader(false);
    }
  }

  @Action
  public async LoadPinnedAssets(lang: string): Promise<void> {
    const storedUser = usersModule.userData;

    if (!storedUser) return;

    this.setPinnedAssetsLoader(true);

    try {
      const response: responseInterface = await api.assets.fetchPinnedAssets(lang, storedUser.userId as number);

      if (response.statusCode !== 200 || !Array.isArray(response.data)) {
        throw new Error("Pinned assets response is invalid");
      }

      this.setPinnedAssetsData(response.data);
    } catch (error) {
      console.error("Error loading pinned assets:", error);
    } finally {
      this.setPinnedAssetsLoader(false);
    }
  }

  @Action
  public async PinAsset(payload: {
    lang: string;
    assetId: string | undefined;
  }): Promise<void> {
    const { lang, assetId } = payload;
    const storedUser = usersModule.userData;

    if (!storedUser) return;

    try {
      if (!assetId) return;

      await api.assets.pin(lang, storedUser.userId as number, assetId);
      await this.LoadPinnedAssets(lang);
    } catch (error) {
      console.error("Error pinning asset:", error);
    }
  }

  @Action
  public async UnpinAsset(payload: {
    lang: string;
    assetId: string | undefined;
  }): Promise<void> {
    const { lang, assetId } = payload;
    const storedUser = usersModule.userData;

    if (!storedUser || !assetId) return;

    try {
      await api.assets.unpin(lang, storedUser.userId as number, assetId);
      await this.LoadPinnedAssets(lang);
    } catch (error) {
      console.error("Error unpinning asset:", error);
    }
  }

  @Action
  public SortPinnedAssets(payload: {
    lang: string;
    ids: string[];
  }): void {
    const { lang, ids } = payload;
    const storedUser = usersModule.userData;

    if (!storedUser || !storedUser.userId) return;

    api.assets.sortPinnedAssets(lang, storedUser.userId as number, ids);
  }

  @Action
  public async FilterAlbumsByCustomFilter(
    filters: FiltersInterface
  ): Promise<FoldersWithAssetsInterface[]> {
    const albums = getAlbumsAssociatedWithAssets(
      this.getAlbumsWithoutAssets,
      filterAssets(this.getAssets, filters)
    );

    return generateAlbumTree(albums);
  }

  @Action
  public Filters(filters: FiltersInterface): void {
    this.setFilters(filters);
  }

  @Action
  public ClearFilters(filtersToClear?: (keyof FiltersInterface)[]): void {
    const next = clearFilters(this.filters, filtersToClear);
    this.setFilters(next);
  }

  @Mutation
  private setFilters(filters: FiltersInterface): void {
    this.filters = filters;
  }

  @Mutation
  private setAssetsData(assets: AssetsInterface[]): void {
    this.assets = assets;
  }

  @Mutation
  public setFilteredAlbums(albums: FoldersWithAssetsInterface[]): void {
    this.filteredAlbums = albums;
  }

  @Mutation
  public setPinnedAssetsData(assets: AssetsInterface[]): void {
    this.pinnedAssets = assets;
  }

  @Mutation
  private setGlobalLoader(loader: boolean): void {
    this.globalLoader = loader;
  }

  @Mutation
  private setAllDataLoader(loader: boolean): void {
    this.allDataLoader = loader;
  }

  @Mutation
  private setAssetsLoader(loader: boolean): void {
    this.assetsLoader = loader;
  }

  @Mutation
  private setPinnedAssetsLoader(loader: boolean): void {
    this.pinnedAssetsLoader = loader;
  }

  @Mutation
  private setAlbumsData(albums: FolderDataInterface[]): void {
    this.albumsWithoutAssets = albums;
  }

  @Mutation
  private setAlbumsAssociatedWithAssetsData(
    albums: FolderDataInterface[]
  ): void {
    this.albums = albums;
  }

  @Mutation
  private setTagsData(tags: TagsInterface[]): void {
    this.tags = tags;
  }

  get getAssetsData(): AssetsInterface[] {
    return [...this.assets].sort((a, b) => a.npk - b.npk);
  }

  get getAssets(): AssetsInterface[] {
    return this.assets;
  }

  get getNewestAssets(): AssetsInterface[] {
    const assets = this.assets;
    if (!assets || assets.length === 0) return [];

    return assets
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .slice(0, 10);
  }

  get getFoldersWithAssets(): FoldersWithAssetsInterface[] {
    const albums: FolderDataInterface[] = [...this.getAlbums];
    return generateAlbumTree(albums);
  }

  get getAlbums(): FolderDataInterface[] {
    return this.albums;
  }

  get getAlbumsWithoutAssets(): FolderDataInterface[] {
    return this.albumsWithoutAssets;
  }

  get getFilteredAlbums(): FoldersWithAssetsInterface[] {
    return this.filteredAlbums;
  }

  get getTags(): TagsInterface[] {
    return this.tags;
  }

  get getGlobalLoader(): boolean {
    return this.globalLoader;
  }

  get getAllDataLoader(): boolean {
    return this.allDataLoader;
  }

  get getAssetsLoader(): boolean {
    return this.assetsLoader;
  }

  get getPinnedAssetsLoader(): boolean {
    return this.pinnedAssetsLoader;
  }

  get getPinnedAssets(): AssetsInterface[] {
    return this.pinnedAssets;
  }

  get getFilters(): FiltersInterface {
    return this.filters;
  }

  get hasFilterActive(): boolean {
    const filterValues = Object.values(this.getFilters);
    return filterValues.some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return (value as string).length > 0;
    });
  }
}
